import { BehaviorSubject, Subject, timer } from 'rxjs';
import type { Observable } from 'rxjs';

export type WSReadyState = 'connecting' | 'open' | 'closing' | 'closed';

export type WSOptions<TIn = unknown, TOut = unknown> = ReturnType<
  typeof defaultOptions<TIn, TOut>
>;
const defaultOptions = <TIn = unknown, TOut = unknown>() => {
  return {
    binaryType: 'blob' as 'blob' | 'arraybuffer',
    maxReconnectAttempts: 1,
    retry: false,
    retryIntervalMs: 1000,
    protocols: undefined as string | string[] | undefined,
    deserializer: (event: MessageEvent): TIn => {
      const data = (event as MessageEvent).data;
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch {
          return data as TIn;
        }
      }
      return data;
    },
    serializer: (value: TOut): string | ArrayBuffer | Blob => {
      if (
        typeof value === 'string' ||
        value instanceof Blob ||
        value instanceof ArrayBuffer
      ) {
        return value;
      }
      return JSON.stringify(value);
    },
  };
};

/**
 * A small, typed RxJS wrapper around the browser WebSocket API.
 * - Inbound messages are exposed as Observable<TIn>
 * - Outbound messages are sent via send(value)
 * - Connection lifecycle is observable via open$, close$, error$, status$
 */
export class WS<TIn = unknown, TOut = unknown> {
  #socket: WebSocket | null = null;
  #reconnectAttempts = 0;

  private readonly errorSubject = new Subject<Event>();
  private readonly messageSubject = new Subject<TIn>();
  private readonly statusSubject = new BehaviorSubject<WSReadyState>('closed');

  readonly error$: Observable<Event> = this.errorSubject.asObservable();
  readonly messages$: Observable<TIn> = this.messageSubject.asObservable();
  readonly status$: Observable<WSReadyState> =
    this.statusSubject.asObservable();

  constructor(
    private readonly url: string,
    private readonly options: WSOptions<TIn, TOut>,
  ) {}

  /** Establish the WebSocket connection */
  connect(): void {
    // Prevent duplicate connections
    if (
      this.#socket &&
      (this.#socket.readyState === WebSocket.OPEN ||
        this.#socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.statusSubject.next('connecting');

    const socket = this.options.protocols
      ? new WebSocket(this.url, this.options.protocols)
      : new WebSocket(this.url);

    socket.binaryType = this.options.binaryType;

    socket.onopen = (ev) => {
      this.statusSubject.next('open');
    };

    socket.onmessage = (ev) => {
      try {
        const value = this.options.deserializer(ev);
        this.messageSubject.next(value);
      } catch (err) {
        // Forward as an error event for subscribers
        this.errorSubject.next(new Event('error'));
      }
    };

    socket.onerror = (ev) => {
      this.errorSubject.next(ev);
    };

    socket.onclose = (ev) => {
      this.statusSubject.next('closed');

      // Attempt reconnection if enabled and not a normal closure
      if (this.options.retry && ev.code !== 1000) {
        this.#tryReconnect();
      }
    };

    this.#socket = socket;
  }

  /** Send a message through the WebSocket. */
  send(value: TOut): void {
    if (!this.#socket || this.#socket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not open');
    }

    const payload = this.options.serializer(value);
    this.#socket.send(payload);
  }

  /** Close the WebSocket connection and disable further reconnects. */
  close(code = 1000, reason?: string): void {
    this.options.retry = false;
    if (this.#socket) {
      this.statusSubject.next('closing');
      this.#socket.close(code, reason);
      this.#socket = null;
    }
  }

  /** Forcefully stop and reset reconnection attempts without closing the current socket. */
  disableReconnect(): void {
    this.options.retry = false;
  }

  #tryReconnect(): void {
    if (this.#reconnectAttempts >= this.options.maxReconnectAttempts) {
      return;
    }

    this.#reconnectAttempts += 1;
    const backoffMs = this.#getBackoffDelay(
      this.#reconnectAttempts,
      this.options.retryIntervalMs,
    );

    this.statusSubject.next('connecting');
    timer(backoffMs).subscribe(() => this.connect());
  }

  #getBackoffDelay(attempt: number, baseMs: number): number {
    // Exponential backoff with jitter
    const exp = Math.min(attempt, 6);
    const delay = baseMs * 2 ** (exp - 1);
    const jitter = Math.floor(Math.random() * (baseMs / 2));
    return delay + jitter;
  }
}

export const wsBuilder = <TIn = unknown, TOut = unknown>(url: string) => {
  const options = defaultOptions<TIn, TOut>();

  const fns = {
    withRetry: (attempts = 3, interval = 1000) => {
      options.retry = true;
      options.maxReconnectAttempts = attempts;
      options.retryIntervalMs = interval;
      return fns;
    },
    withProtocols: (protocols: string | string[]) => {
      options.protocols = protocols;
      return fns;
    },
    withDeserializer: (deserializer: typeof options.deserializer) => {
      options.deserializer = deserializer;
      return fns;
    },
    withSerializer: (serializer: typeof options.serializer) => {
      options.serializer = serializer;
      return fns;
    },
    create: () => {
      const ws = new WS(url, options);
      return ws;
    },
  };
  return fns;
};
